import type { Dispatch } from '@reduxjs/toolkit';
import React from 'react';
import {
  openCreditNotAvailableModal,
  setSelectedLeads,
  setIsSelectedAllLeads,
  setDeSelectedLeads,
  setSelectedCompanies,
  setIsSelectedAllCompanies,
} from '../../v2-lead-slice';
import {
  LeadRevealType,
  SavedTabFilters,
  LeadFinderPrimaryTab,
  LeadFinderSecondaryTab,
  LeadFinderListTab,
  LeadFinderList,
  LeadFinderListVisibility,
} from '../../types';
import { dateToString } from '../../../../shared/functions/date';
import {
  getUserTrackingId,
  getUserShAccountId,
  getUserPlanName,
  getUserRole,
  getUserLeadFinderPlanName,
  leadFinderBulkOperationLimit,
} from '../../../../shared/utils/user-details';
import { AnalyticsEvents } from '../../../../shared/enums/analytics';
import {
  getSavedLeads,
  getSavedCompanies,
  addToListsActionAPI,
  addToListsBulkActionAPI,
  revealCompaniesAPI,
} from '../../helpers/leads.api';
import { enableBulkActionRequestPolling } from '../../../home/home-slice';
import toaster, { Theme } from '../../../../shared/toaster';

export interface ListManagementContext {
  dispatch: Dispatch<any>;
  selectedLeads: number[];
  selectedCompanies: number[];
  isSelectedAllLeads: boolean;
  isSelectedAllCompanies: boolean;
  companiesData: any;
  activeSubTab: LeadFinderSecondaryTab;
  activeLeadType: LeadFinderPrimaryTab;
  isCompanyTab: boolean;
  apiParam: any;
  savedApiParam: any;
  companySavedApiParam: any;
  companyApiParam?: any;
  savedFilters: SavedTabFilters;
  leadCredits: number;
  handleGeneratePayload: () => any;
  handleCompanyGeneratePayload: () => any;
  refreshListsAndSavedData: () => Promise<void>;
  refreshPeopleLists: () => void;
  refreshCompanyLists: () => void;
  setEnrichingCompanies: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  setIsCreateListLoading: (val: boolean) => void;
  setLeadsData: (data: any) => void;
  setCompaniesData: (data: any) => void;
  savedSearchFiltersToUseRef: React.MutableRefObject<any>;
  refreshLeadsAndStartPollingRef: React.MutableRefObject<() => void>;
  maybeEnableNotificationPollingRef: React.MutableRefObject<(res: any) => void>;
  companyIdToAddFromRowRef: React.MutableRefObject<number | null>;
  getEffectiveSelectedLeads: () => number[];
  getEffectiveSelectedCompanies: () => number[];
  handleBulkRevealResponse?: (res: any, ids: number[]) => void;
  newLists?: Array<{ name: string; visibility: string; teamIds?: number[] }>;
  listIds?: number[];
  normalizeListIdsForPayload: (listIds: any) => any;
}

/**
 * Builds searchFilters for Saved tab API calls.
 * People tab uses `tags` key; Company tab uses `tagIds` key.
 */
export function buildSavedSearchFilters(
  savedFilters: SavedTabFilters,
  savedApiParam: any,
  isPeopleTab: boolean,
): Record<string, any> {
  const searchFilters: Record<string, any> = {
    start: savedApiParam?.start || 1,
    take: savedApiParam?.take || 25,
  };

  if (savedFilters.search) {
    searchFilters.search = savedFilters.search;
  }

  if (savedFilters.tags && savedFilters.tags.length > 0) {
    if (isPeopleTab) {
      searchFilters.tags = savedFilters.tags;
    } else {
      searchFilters.tagIds = savedFilters.tags;
    }
  }

  if (savedFilters.createdDate && savedFilters.createdDate.length === 2) {
    const [startDate, endDate] = savedFilters.createdDate;
    searchFilters.createdDate = [
      dateToString(startDate),
      dateToString(endDate),
    ];
  }

  if (
    savedFilters.listIds &&
    (savedFilters.listIds.includes?.length > 0 ||
      savedFilters.listIds.excludes?.length > 0)
  ) {
    const listIdsFilter: any = {};
    if (savedFilters.listIds.includes?.length > 0) {
      listIdsFilter.includes = savedFilters.listIds.includes;
    }
    if (savedFilters.listIds.excludes?.length > 0) {
      listIdsFilter.excludes = savedFilters.listIds.excludes;
    }
    if (Object.keys(listIdsFilter).length > 0) {
      searchFilters.listIds = listIdsFilter;
    }
  }

  if (savedFilters.excludeProspects) {
    searchFilters.excludeProspects = savedFilters.excludeProspects;
  }

  return searchFilters;
}

/**
 * Transforms getSavedLeads/getSavedCompanies API response into a normalized shape.
 * Returns null if res.payload is absent.
 */
export function normalizeSavedResponse(
  res: any,
  fallbackParam: any,
  profileKey: 'profiles' | 'companies',
): {
  profiles: any[];
  pagination: { page: number; limit: number; totalRows: number };
} | null {
  if (!res?.payload) return null;

  const savedPayload = res.payload || {};
  const savedProfiles = Array.isArray(savedPayload)
    ? savedPayload
    : savedPayload?.[profileKey] || [];
  const savedPaginationSource = Array.isArray(savedPayload)
    ? null
    : savedPayload?.pagination;

  const takeValue = savedPaginationSource?.take || fallbackParam?.take || 25;
  const startValue = savedPaginationSource?.start || fallbackParam?.start || 1;
  const pageNumber = Math.ceil(startValue / takeValue);

  const pagination = savedPaginationSource
    ? {
        page: pageNumber,
        limit: takeValue,
        totalRows: savedPaginationSource.total || 0,
      }
    : { page: pageNumber, limit: takeValue, totalRows: savedProfiles.length };

  return { profiles: [...savedProfiles], pagination };
}

/**
 * Clears people selection state via Redux dispatch.
 */
export function clearPeopleSelection(dispatch: Dispatch<any>): void {
  dispatch(setSelectedLeads([]));
  dispatch(setIsSelectedAllLeads(false));
  dispatch(setDeSelectedLeads([]));
}

/**
 * Clears company selection state via Redux dispatch.
 */
export function clearCompanySelection(dispatch: Dispatch<any>): void {
  dispatch(setSelectedCompanies([]));
  dispatch(setIsSelectedAllCompanies(false));
}

/**
 * Checks if lead credits are sufficient for company reveal (0.1 credits per company).
 * Returns true if sufficient. Returns false and fires dispatch + analytics if not.
 */
export function checkCompanyCredits(
  rowCompanyId: number | null,
  isSelectedAllCompanies: boolean,
  companiesData: any,
  effectiveSelectedCompanies: number[],
  leadCredits: number,
  dispatch: Dispatch<any>,
): boolean {
  let companyCount = 0;
  if (rowCompanyId != null) {
    companyCount = 1;
  } else if (isSelectedAllCompanies) {
    companyCount = Math.min(
      companiesData?.pagination?.totalRows ?? 0,
      leadFinderBulkOperationLimit(),
    );
  } else {
    companyCount = effectiveSelectedCompanies.length;
  }

  const creditsRequired = companyCount * 0.1;
  if (Number(leadCredits) < creditsRequired) {
    dispatch(openCreditNotAvailableModal(LeadRevealType.COMPANY));
    window.analytics?.track?.({
      userId: getUserTrackingId(),
      event: AnalyticsEvents.TrippedFeature,
      properties: {
        shAccountId: getUserShAccountId(),
        current_plan: getUserPlanName(),
        role: getUserRole(),
        'LF Plan': getUserLeadFinderPlanName(),
        feature_type: 'Lead Finder',
      },
    });
    return false;
  }
  return true;
}

// ─── handleSaveList extracted branch functions ──────────────────────────────

export async function saveListCompanyTotalNew(
  ctx: ListManagementContext,
  params: {
    listName: string;
    visibility: LeadFinderListVisibility;
    teamIds: number[];
    shouldEnrich: boolean;
    revealTypeToUse: LeadRevealType;
  },
): Promise<void> {
  const rowCompanyId = ctx.companyIdToAddFromRowRef.current;
  const effectiveSelectedCompanies =
    rowCompanyId != null ? [rowCompanyId] : ctx.selectedCompanies;
  const totalCompanies = Math.min(
    ctx.isSelectedAllCompanies
      ? ctx.companiesData?.pagination?.totalRows
      : effectiveSelectedCompanies.length,
    leadFinderBulkOperationLimit(),
  );

  const newList: any = {
    name: params.listName,
    visibility: params.visibility,
  };
  if (params.teamIds.length > 0) {
    newList.teamIds = params.teamIds;
  }

  const revealPayload: any = {
    shouldEnrich: params.shouldEnrich,
    totalCompanies,
    newLists: [newList],
  };

  if (ctx.isSelectedAllCompanies) {
    const filtersPayload = ctx.handleCompanyGeneratePayload();
    const { page, ...filtersWithoutPage } = filtersPayload || {};
    revealPayload.filters = {
      ...filtersWithoutPage,
      tab: ctx.activeSubTab,
    };
  } else {
    revealPayload.companyIds = effectiveSelectedCompanies;
  }

  const revealResponse = await revealCompaniesAPI(revealPayload);
  await ctx.refreshListsAndSavedData();
  ctx.dispatch(setSelectedCompanies([]));
  ctx.dispatch(setIsSelectedAllCompanies(false));
  ctx.companyIdToAddFromRowRef.current = null;

  const revealJobId =
    revealResponse?.payload?.jobId ??
    revealResponse?.payload?.jobID ??
    revealResponse?.payload?.job_id;
  if (revealJobId) {
    toaster.success('Company Enriched Successfully', { theme: Theme.New });
  } else {
    toaster.success(
      'It will take time to enrich companies, check notifications for updates.',
      { theme: Theme.New },
    );
  }
}

export async function saveListPeopleNonSaved(
  ctx: ListManagementContext,
  params: {
    listName: string;
    visibility: LeadFinderListVisibility;
    teamIds: number[];
    shouldEnrich: boolean;
    revealTypeToUse: LeadRevealType;
    activeTab: LeadFinderListTab;
  },
  subTab: 'total' | LeadFinderSecondaryTab.NEW,
): Promise<void> {
  ctx.setIsCreateListLoading(false);
  const newList: any = { name: params.listName, visibility: params.visibility };
  if (params.teamIds.length > 0) {
    newList.teamIds = params.teamIds;
  }

  const tabValue = LeadFinderListTab.People;
  const isSelectedAll = ctx.isSelectedAllLeads;

  let response;
  if (isSelectedAll) {
    const filtersPayload = ctx.handleGeneratePayload();
    const { page, ...filtersWithoutPage } = filtersPayload;
    const searchFilters = {
      ...filtersWithoutPage,
      page: ctx.apiParam?.page || 1,
    };

    const bulkPayload: any = {
      actionType: LeadFinderList.AddToList,
      tab: tabValue,
      subTab,
      name: newList.name,
      visibility: newList.visibility,
      searchFilters,
      revealType: params.revealTypeToUse,
    };
    if (newList.teamIds && newList.teamIds.length > 0) {
      bulkPayload.teamIds = newList.teamIds;
    }

    if (subTab === 'total' && ctx.isCompanyTab && params.shouldEnrich) {
      const rowCompanyId = ctx.companyIdToAddFromRowRef.current;
      const effectiveSelectedCompanies =
        rowCompanyId != null ? [rowCompanyId] : ctx.selectedCompanies;
      const companyIdsToMark =
        isSelectedAll && ctx.companiesData?.profiles?.length
          ? ctx.companiesData.profiles
              .map((c) => c.id || c.company_id)
              .filter((id) => id !== null && id !== undefined)
          : effectiveSelectedCompanies;
      ctx.setEnrichingCompanies((prev) => {
        const updated = { ...prev };
        (companyIdsToMark || []).forEach((id: any) => {
          if (id) updated[id] = true;
        });
        return updated;
      });
    }

    response = await addToListsBulkActionAPI(bulkPayload);
    await ctx.refreshListsAndSavedData();
  } else {
    const actionPayload: any = {
      actionType: LeadFinderList.AddToList,
      tab: tabValue,
      subTab: subTab === 'total' ? LeadFinderSecondaryTab.TOTAL : subTab,
      resourceIds: ctx.getEffectiveSelectedLeads(),
      name: newList.name,
      visibility: newList.visibility,
    };
    if (newList.teamIds && newList.teamIds.length > 0) {
      actionPayload.teamIds = newList.teamIds;
    }
    if (params.activeTab === LeadFinderListTab.People) {
      actionPayload.revealType = params.revealTypeToUse;
    }
    if (params.activeTab === LeadFinderListTab.Company) {
      actionPayload.shouldEnrich = params.shouldEnrich;
    }
    response = await addToListsActionAPI(actionPayload);
    if (subTab === 'total') {
      await ctx.refreshListsAndSavedData();
    }
    if (
      actionPayload?.resourceIds &&
      actionPayload?.resourceIds?.length > 1
    ) {
      ctx.dispatch(enableBulkActionRequestPolling());
    }
  }

  if (subTab === 'total') {
    ctx.maybeEnableNotificationPollingRef.current(response);
  }
  toaster.success(
    response?.message || response?.successMessage || 'List created successfully',
    { theme: Theme.New },
  );
  clearPeopleSelection(ctx.dispatch);
  ctx.refreshLeadsAndStartPollingRef.current();
  ctx.refreshPeopleLists();
}

export async function saveListPeopleSaved(
  ctx: ListManagementContext,
  params: {
    listName: string;
    visibility: LeadFinderListVisibility;
    teamIds: number[];
    shouldEnrich: boolean;
    revealTypeToUse: LeadRevealType;
    activeTab: LeadFinderListTab;
  },
): Promise<void> {
  ctx.setIsCreateListLoading(false);
  const tabValue = LeadFinderListTab.People;
  const isSelectedAll = ctx.isSelectedAllLeads;

  let response;
  if (isSelectedAll) {
    const searchFilters = buildSavedSearchFilters(ctx.savedFilters, ctx.savedApiParam, true);
    const bulkSavedPayload: any = {
      actionType: LeadFinderList.AddToList,
      tab: tabValue,
      subTab: LeadFinderSecondaryTab.SAVED,
      name: params.listName,
      visibility: params.visibility,
      searchFilters,
      revealType: params.revealTypeToUse,
    };
    ctx.savedSearchFiltersToUseRef.current = searchFilters;
    if (params.teamIds.length > 0) {
      bulkSavedPayload.teamIds = params.teamIds;
    }
    response = await addToListsBulkActionAPI(bulkSavedPayload);
  } else {
    const actionPayload: any = {
      actionType: LeadFinderList.AddToList,
      tab: tabValue,
      subTab: LeadFinderSecondaryTab.SAVED,
      resourceIds: ctx.getEffectiveSelectedLeads(),
      name: params.listName,
      visibility: params.visibility,
    };
    if (params.teamIds.length > 0) {
      actionPayload.teamIds = params.teamIds;
    }
    if (params.activeTab === LeadFinderListTab.People) {
      actionPayload.revealType = params.revealTypeToUse;
    }
    if (params.activeTab === LeadFinderListTab.Company) {
      actionPayload.shouldEnrich = params.shouldEnrich;
    }
    response = await addToListsActionAPI(actionPayload);
    if (
      actionPayload?.resourceIds &&
      actionPayload?.resourceIds?.length > 1
    ) {
      ctx.dispatch(enableBulkActionRequestPolling());
    }
  }

  toaster.success(
    response?.message || response?.successMessage || 'List created successfully',
    { theme: Theme.New },
  );
  clearPeopleSelection(ctx.dispatch);

  const currentSavedApiParam = ctx.savedApiParam;
  const payloadForSaved: any = {
    start: currentSavedApiParam?.start || 1,
    take: currentSavedApiParam?.take || 25,
  };
  const sourceFilters =
    (ctx.savedSearchFiltersToUseRef.current as any) ?? ctx.savedFilters;
  if (sourceFilters?.search) {
    payloadForSaved.search = sourceFilters.search;
  }
  if (sourceFilters?.tags && sourceFilters.tags.length > 0) {
    payloadForSaved.tagIds = sourceFilters.tags;
  } else if (sourceFilters?.tagIds) {
    payloadForSaved.tagIds = sourceFilters.tagIds;
  }
  if (
    sourceFilters?.createdDate &&
    sourceFilters.createdDate.length === 2
  ) {
    const [startDate, endDate] = sourceFilters.createdDate;
    payloadForSaved.createdDate = `${dateToString(
      startDate,
    )}-${dateToString(endDate)}`;
  }
  if (sourceFilters?.listIds) {
    payloadForSaved.listIds = sourceFilters.listIds;
  }
  if (sourceFilters?.excludeProspects) {
    payloadForSaved.excludeProspects = sourceFilters.excludeProspects;
  }

  const res = await getSavedLeads(payloadForSaved);
  const normalized = normalizeSavedResponse(res, payloadForSaved, 'profiles');
  if (normalized) {
    ctx.setLeadsData(normalized);
  }
  ctx.refreshPeopleLists();
}

export async function saveListCompanySaved(
  ctx: ListManagementContext,
  params: {
    listName: string;
    visibility: LeadFinderListVisibility;
    teamIds: number[];
    shouldEnrich: boolean;
    revealTypeToUse: LeadRevealType;
    activeTab: LeadFinderListTab;
  },
): Promise<void> {
  ctx.setIsCreateListLoading(false);
  const isCompany = ctx.activeLeadType === LeadFinderPrimaryTab.COMPANY;
  const tabValue = LeadFinderListTab.Company;
  const isSelectedAll = ctx.isSelectedAllCompanies;

  let response;
  if (isSelectedAll) {
    const searchFilters = buildSavedSearchFilters(ctx.savedFilters, ctx.companySavedApiParam, false);
    const bulkSavedPayload: any = {
      actionType: LeadFinderList.AddToList,
      tab: tabValue,
      subTab: LeadFinderSecondaryTab.SAVED,
      name: params.listName,
      visibility: params.visibility,
      searchFilters,
      shouldEnrich: params.shouldEnrich,
    };
    if (params.teamIds.length > 0) {
      bulkSavedPayload.teamIds = params.teamIds;
    }
    response = await addToListsBulkActionAPI(bulkSavedPayload);
  } else {
    const actionPayload: any = {
      actionType: LeadFinderList.AddToList,
      tab: tabValue,
      subTab: LeadFinderSecondaryTab.SAVED,
      resourceIds: ctx.getEffectiveSelectedCompanies(),
      name: params.listName,
      visibility: params.visibility,
    };
    if (params.teamIds.length > 0) {
      actionPayload.teamIds = params.teamIds;
    }
    if (params.activeTab === LeadFinderListTab.People) {
      actionPayload.revealType = params.revealTypeToUse;
    }
    if (params.activeTab === LeadFinderListTab.Company) {
      actionPayload.shouldEnrich = params.shouldEnrich;
    }
    response = await addToListsActionAPI(actionPayload);
    if (
      actionPayload?.resourceIds &&
      actionPayload?.resourceIds?.length > 1
    ) {
      ctx.dispatch(enableBulkActionRequestPolling());
    }
  }

  toaster.success(
    response?.message || response?.successMessage || 'List created successfully',
    { theme: Theme.New },
  );
  clearCompanySelection(ctx.dispatch);

  const currentSavedApiParam = ctx.companySavedApiParam;
  const payloadForSaved: any = {
    start: currentSavedApiParam?.start || 1,
    take: currentSavedApiParam?.take || 25,
  };
  const sourceFilters =
    (ctx.savedSearchFiltersToUseRef.current as any) ?? ctx.savedFilters;
  if (sourceFilters?.search) {
    payloadForSaved.search = sourceFilters.search;
  }
  if (sourceFilters?.tags && sourceFilters.tags.length > 0) {
    payloadForSaved.tagIds = sourceFilters.tags;
  } else if (sourceFilters?.tagIds) {
    payloadForSaved.tagIds = sourceFilters.tagIds;
  }
  if (
    sourceFilters?.createdDate &&
    sourceFilters.createdDate.length === 2
  ) {
    const [startDate, endDate] = sourceFilters.createdDate;
    payloadForSaved.createdDate = `${dateToString(
      startDate,
    )}-${dateToString(endDate)}`;
  }
  if (sourceFilters?.listIds) {
    payloadForSaved.listIds = sourceFilters.listIds;
  }
  if (sourceFilters?.excludeProspects) {
    payloadForSaved.excludeProspects = sourceFilters.excludeProspects;
  }

  const res = isCompany
    ? await getSavedCompanies(payloadForSaved)
    : await getSavedLeads(payloadForSaved);
  const normalized = normalizeSavedResponse(res, payloadForSaved, 'companies');
  if (normalized) {
    ctx.setCompaniesData(normalized);
  }
  ctx.refreshCompanyLists();
}

// ─── handleAddToList extracted branch functions ──────────────────────────────

export async function addToListCompanyTotalNew(
  ctx: ListManagementContext,
  params: {
    shouldEnrich: boolean;
    revealType: LeadRevealType;
    isSelectedAll: boolean;
    effectiveSelectedCompanies: number[];
  },
): Promise<any> {
  const totalCompanies = Math.min(
    params.isSelectedAll
      ? ctx.companiesData?.pagination?.totalRows
      : params.effectiveSelectedCompanies.length,
    leadFinderBulkOperationLimit(),
  );
  const revealPayload: any = {
    shouldEnrich: params.shouldEnrich,
    totalCompanies,
    listIds: ctx.listIds,
  };

  if (params.isSelectedAll) {
    const filtersPayload = ctx.handleCompanyGeneratePayload();
    const { page, ...filtersWithoutPage } = filtersPayload;
    revealPayload.filters = {
      ...filtersWithoutPage,
      tab: ctx.activeSubTab,
    };
  } else {
    revealPayload.companyIds = params.effectiveSelectedCompanies;
  }

  if (
    revealPayload?.shouldEnrich &&
    params.effectiveSelectedCompanies &&
    params.effectiveSelectedCompanies.length > 0
  ) {
    ctx.setEnrichingCompanies((prev) => ({
      ...prev,
      ...Object.fromEntries(
        params.effectiveSelectedCompanies.map((id) => [id, true]),
      ),
    }));
  }

  const response = await revealCompaniesAPI(revealPayload);
  await ctx.refreshListsAndSavedData();
  try {
    const revealJobId =
      response?.payload?.jobId ??
      response?.payload?.jobID ??
      response?.payload?.job_id;
    if (revealJobId) {
      toaster.success('Company Enriched Successfully', { theme: Theme.New });
    } else {
      toaster.success(
        'It will take time to enrich companies, check notifications for updates.',
        { theme: Theme.New },
      );
    }
  } catch (_e) {
    // ignore UI error and continue
  }

  if (response && ctx.handleBulkRevealResponse) {
    const companyIdsToEnrich = params.isSelectedAll
      ? ctx.companiesData?.profiles
          ?.map((c) => c.id || c.company_id)
          .filter(
            (id): id is number => id !== null && id !== undefined,
          ) || []
      : params.effectiveSelectedCompanies;
    ctx.handleBulkRevealResponse(response, companyIdsToEnrich);
  }
  return response;
}

export async function addToListSavedBulk(
  ctx: ListManagementContext,
  params: {
    shouldEnrich: boolean;
    revealType: LeadRevealType;
    isSelectedAll: boolean;
    effectiveSelectedCompanies: number[];
  },
): Promise<any> {
  const isPeopleTab = ctx.activeLeadType === LeadFinderPrimaryTab.PEOPLE;
  const currentSavedApiParam = isPeopleTab
    ? ctx.savedApiParam
    : ctx.companySavedApiParam;
  const searchFilters = buildSavedSearchFilters(
    ctx.savedFilters,
    currentSavedApiParam,
    isPeopleTab,
  );
  ctx.savedSearchFiltersToUseRef.current = searchFilters;

  const bulkSavedPayload: any = {
    actionType: LeadFinderList.AddToList,
    tab: isPeopleTab ? LeadFinderListTab.People : LeadFinderListTab.Company,
    subTab: LeadFinderSecondaryTab.SAVED,
    searchFilters,
    revealType: isPeopleTab ? params.revealType : undefined,
    ...(ctx.isCompanyTab && { shouldEnrich: params.shouldEnrich }),
  };

  if (ctx.newLists && ctx.newLists.length > 0) {
    const newList = ctx.newLists[0];
    bulkSavedPayload.name = newList.name;
    bulkSavedPayload.visibility = newList.visibility;
    if (newList.teamIds && newList.teamIds.length > 0) {
      bulkSavedPayload.teamIds = newList.teamIds;
    }
  } else if (ctx.listIds && ctx.listIds.length > 0) {
    bulkSavedPayload.listIds = ctx.listIds;
  }

  if (ctx.isCompanyTab && params.shouldEnrich) {
    const companyIdsToMark =
      params.isSelectedAll && ctx.companiesData?.profiles?.length
        ? ctx.companiesData.profiles
            .map((c) => c.id || c.company_id)
            .filter((id) => id !== null && id !== undefined)
        : params.effectiveSelectedCompanies;
    ctx.setEnrichingCompanies((prev) => {
      const updated = { ...prev };
      (companyIdsToMark || []).forEach((id: any) => {
        if (id) updated[id] = true;
      });
      return updated;
    });
  }

  return addToListsBulkActionAPI(bulkSavedPayload);
}

export async function addToListPeopleBulk(
  ctx: ListManagementContext,
  subTab: LeadFinderSecondaryTab | 'total',
  params: { revealType: LeadRevealType },
): Promise<any> {
  const filtersPayload = ctx.handleGeneratePayload();
  const { page, ...filtersWithoutPage } = filtersPayload;
  const searchFilters = {
    ...filtersWithoutPage,
    page: ctx.apiParam?.page || 1,
  };

  const bulkPayload: any = {
    actionType: LeadFinderList.AddToList,
    tab: LeadFinderListTab.People,
    subTab,
    searchFilters,
    revealType: params.revealType,
  };

  if (ctx.newLists && ctx.newLists.length > 0) {
    const newList = ctx.newLists[0];
    bulkPayload.name = newList.name;
    bulkPayload.visibility = newList.visibility;
    if (newList.teamIds && newList.teamIds.length > 0) {
      bulkPayload.teamIds = newList.teamIds;
    }
  } else if (ctx.listIds && ctx.listIds.length > 0) {
    bulkPayload.listIds = ctx.listIds;
  }

  return addToListsBulkActionAPI(bulkPayload);
}

export async function addToListPeopleSingle(
  ctx: ListManagementContext,
  subTab: LeadFinderSecondaryTab,
  params: { revealType: LeadRevealType },
): Promise<any> {
  const payload: any = {
    actionType: LeadFinderList.AddToList,
    tab: LeadFinderListTab.People,
    subTab,
    resourceIds: ctx.getEffectiveSelectedLeads(),
    revealType: params.revealType,
  };

  if (ctx.newLists && ctx.newLists.length > 0) {
    const newList = ctx.newLists[0];
    payload.name = newList.name;
    payload.visibility = newList.visibility;
    if (newList.teamIds && newList.teamIds.length > 0) {
      payload.teamIds = newList.teamIds;
    }
  } else if (ctx.listIds && ctx.listIds.length > 0) {
    payload.listIds = ctx.listIds;
  }
  // else: No list selected — backend handles adding without explicit list selection.

  const response = await addToListsActionAPI(payload);
  if (payload?.resourceIds && payload?.resourceIds?.length > 1) {
    ctx.dispatch(enableBulkActionRequestPolling());
  }
  return response;
}

export async function addToListActionFallback(
  ctx: ListManagementContext,
  params: {
    shouldEnrich: boolean;
    revealType: LeadRevealType;
    effectiveSelectedCompanies: number[];
  },
): Promise<any> {
  const isPeopleTab = ctx.activeLeadType === LeadFinderPrimaryTab.PEOPLE;
  const subTabMap: Record<string, LeadFinderSecondaryTab> = {
    [LeadFinderSecondaryTab.SAVED]: LeadFinderSecondaryTab.SAVED,
    [LeadFinderSecondaryTab.NEW]: LeadFinderSecondaryTab.NEW,
  };
  const subTab =
    subTabMap[ctx.activeSubTab] ?? LeadFinderSecondaryTab.TOTAL;
  const effectiveSelectedItems = isPeopleTab
    ? ctx.getEffectiveSelectedLeads()
    : ctx.getEffectiveSelectedCompanies();

  if (
    isPeopleTab &&
    params.revealType === LeadRevealType.EMAIL_PHONE &&
    Number(ctx.leadCredits) < effectiveSelectedItems?.length * 7
  ) {
    ctx.dispatch(openCreditNotAvailableModal(LeadRevealType.EMAIL_PHONE));
    window.analytics?.track?.({
      userId: getUserTrackingId(),
      event: AnalyticsEvents.TrippedFeature,
      properties: {
        shAccountId: getUserShAccountId(),
        current_plan: getUserPlanName(),
        role: getUserRole(),
        'LF Plan': getUserLeadFinderPlanName(),
        feature_type: 'Lead Finder',
      },
    });
    return null;
  }

  const payload: any = {
    actionType: LeadFinderList.AddToList,
    tab: isPeopleTab ? LeadFinderListTab.People : LeadFinderListTab.Company,
    subTab,
    resourceIds: effectiveSelectedItems,
    ...(isPeopleTab && { revealType: params.revealType }),
    ...(ctx.isCompanyTab && { shouldEnrich: params.shouldEnrich }),
  };

  if (ctx.newLists && ctx.newLists.length > 0) {
    const newList = ctx.newLists[0];
    payload.name = newList.name;
    payload.visibility = newList.visibility;
    if (newList.teamIds && newList.teamIds.length > 0) {
      payload.teamIds = newList.teamIds;
    }
  } else if (ctx.listIds && ctx.listIds.length > 0) {
    payload.listIds = ctx.listIds;
  }

  if (ctx.isCompanyTab && params.shouldEnrich) {
    const companyIdsToMark =
      ctx.isSelectedAllCompanies && ctx.companiesData?.profiles?.length
        ? ctx.companiesData.profiles
            .map((c) => c.id || c.company_id)
            .filter((id) => id !== null && id !== undefined)
        : params.effectiveSelectedCompanies;
    ctx.setEnrichingCompanies((prev) => {
      const updated = { ...prev };
      (companyIdsToMark || []).forEach((id: any) => {
        if (id) updated[id] = true;
      });
      return updated;
    });
  }

  const response = await addToListsActionAPI(payload);
  if (payload?.resourceIds && payload?.resourceIds?.length > 1) {
    ctx.dispatch(enableBulkActionRequestPolling());
  }
  return response;
}

// ─── handleRemoveFromList extracted branch functions ─────────────────────────

export async function removeFromListSavedBulk(
  ctx: ListManagementContext,
  tabValue: LeadFinderListTab,
  removeListIds: number[],
): Promise<any> {
  const isPeopleTab = ctx.activeLeadType === LeadFinderPrimaryTab.PEOPLE;
  const currentSavedApiParam = isPeopleTab
    ? ctx.savedApiParam
    : ctx.companySavedApiParam;
  const searchFilters = buildSavedSearchFilters(
    ctx.savedFilters,
    currentSavedApiParam,
    isPeopleTab,
  );

  const bulkPayload = {
    actionType: LeadFinderList.RemoveFromList,
    tab: tabValue,
    subTab: LeadFinderSecondaryTab.SAVED,
    listIds: removeListIds,
    searchFilters,
  };
  return addToListsBulkActionAPI(bulkPayload);
}

export async function removeFromListTotalNewBulk(
  ctx: ListManagementContext,
  tabValue: LeadFinderListTab,
  removeListIds: number[],
): Promise<any> {
  const isPeopleTab = ctx.activeLeadType === LeadFinderPrimaryTab.PEOPLE;
  let filtersPayload: any;
  let pageFallback: number;
  if (isPeopleTab) {
    filtersPayload = ctx.handleGeneratePayload();
    pageFallback = ctx.apiParam?.page || 1;
  } else {
    filtersPayload = ctx.handleCompanyGeneratePayload();
    pageFallback = ctx.companyApiParam?.page || 1;
  }
  const { page, ...filtersWithoutPage } = filtersPayload;
  const searchFilters = {
    ...filtersWithoutPage,
    page: pageFallback,
  };

  const bulkPayload = {
    actionType: LeadFinderList.RemoveFromList,
    tab: tabValue,
    subTab:
      ctx.activeSubTab === LeadFinderSecondaryTab.TOTAL ? 'total' : 'netNew',
    listIds: removeListIds,
    searchFilters,
  };
  return addToListsBulkActionAPI(bulkPayload);
}

export async function removeFromListSingle(
  ctx: ListManagementContext,
  tabValue: LeadFinderListTab,
  removeListIds: number[],
): Promise<any> {
  const isPeopleTab = ctx.activeLeadType === LeadFinderPrimaryTab.PEOPLE;
  const subTabMap: Record<string, LeadFinderSecondaryTab> = {
    [LeadFinderSecondaryTab.SAVED]: LeadFinderSecondaryTab.SAVED,
    [LeadFinderSecondaryTab.NEW]: LeadFinderSecondaryTab.NEW,
  };
  const subTab =
    subTabMap[ctx.activeSubTab] ?? LeadFinderSecondaryTab.TOTAL;

  const payload = {
    actionType: LeadFinderList.RemoveFromList,
    tab: tabValue,
    subTab,
    resourceIds: isPeopleTab
      ? ctx.getEffectiveSelectedLeads()
      : ctx.getEffectiveSelectedCompanies(),
    listIds: removeListIds,
  };
  return addToListsActionAPI(payload);
}
